type I18nLocale = {
  code?: string;
  iso?: string;
};

/**
 * @nuxtjs/i18n detectBrowserLanguage uses an in-app navigateTo. On this SSG
 * site that locale switch re-runs useAsyncGql in the browser,
 * graphql.datocms.com returns 401, and pages/index.vue calls showError(404).
 *
 * Redirect on the root path before i18n boots — including when the locale
 * cookie is already set to a non-default language — then hold plugin setup
 * so the SPA locale switch never runs.
 */
export default defineNuxtPlugin({
  name: 'i18n-ssg-locale-redirect',
  enforce: 'pre',
  async setup(nuxtApp) {
    const i18n = useRuntimeConfig().public.i18n;
    const defaultLocale = String(i18n.defaultLocale || 'de');

    nuxtApp.hook(
      'i18n:beforeLocaleSwitch',
      async ({ oldLocale, newLocale }) => {
        if (window.location.pathname !== '/') {
          return;
        }

        const target = newLocale?.substring(0, 2);
        if (!target || target === defaultLocale || target === oldLocale) {
          return;
        }

        window.location.replace(`/${target}`);
        await new Promise(() => {});
      },
    );

    if (window.location.pathname !== '/') {
      return;
    }
    const detectOptions = i18n.detectBrowserLanguage;
    const cookieKey =
      (typeof detectOptions === 'object' && detectOptions?.cookieKey) ||
      COOKIE_KEY;
    const locales = resolveLocales(i18n);
    const localeCodes = locales
      .map((locale) => locale.code)
      .filter((code): code is string => Boolean(code));

    const cookieLocale = readCookie(cookieKey).substring(0, 2);
    const cookieIsKnownLocale = localeCodes.includes(cookieLocale);
    const targetLocale = cookieIsKnownLocale
      ? cookieLocale
      : detectLocaleFromNavigator(locales);

    if (!targetLocale || targetLocale === defaultLocale) {
      return;
    }

    const cookieSecure =
      typeof detectOptions === 'object' && detectOptions?.cookieSecure === true;

    if (!cookieIsKnownLocale) {
      writeCookie(cookieKey, targetLocale, cookieSecure);
    }

    window.location.replace(`/${targetLocale}`);
    await new Promise(() => {});
  },
});

const COOKIE_KEY = 'i18n_redirected';

function resolveLocales(i18n: {
  locales?: unknown;
  configLocales?: I18nLocale[];
}) {
  if (Array.isArray(i18n.configLocales) && i18n.configLocales.length > 0) {
    return i18n.configLocales;
  }

  if (Array.isArray(i18n.locales)) {
    return i18n.locales.map((locale) =>
      typeof locale === 'string' ? { code: locale } : locale,
    );
  }

  if (i18n.locales && typeof i18n.locales === 'object') {
    return Object.keys(i18n.locales).map((code) => ({ code }));
  }

  return [];
}

function readCookie(name: string) {
  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return match ? decodeURIComponent(match.slice(prefix.length)) : '';
}

function writeCookie(name: string, value: string, secureFromConfig: boolean) {
  const secure = secureFromConfig || window.location.protocol === 'https:';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${
    secure ? '; Secure' : ''
  }`;
}

function detectLocaleFromNavigator(locales: I18nLocale[]) {
  const languages =
    navigator.languages?.length > 0
      ? navigator.languages
      : [navigator.language];

  for (const language of languages) {
    const lower = language.toLowerCase();
    const match = locales.find((locale) => {
      const code = locale.code?.toLowerCase();
      const iso = locale.iso?.toLowerCase();
      return (
        (iso && lower === iso) ||
        (code && (lower === code || lower.startsWith(`${code}-`)))
      );
    });

    if (match?.code) {
      return match.code;
    }
  }

  return '';
}
